import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Layers, Plus, Edit2, Check, X, ChevronUp, ChevronDown,
  ToggleLeft, ToggleRight, Search, AlertCircle, Filter,
} from 'lucide-react';
import { api } from '@/lib/api';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const ITEM_TYPES = [
  { key: 'INGREDIENT', label: 'Ingredient', color: '#16a34a' },
  { key: 'PACKAGING', label: 'Packaging', color: '#2563eb' },
  { key: 'CONSUMABLE', label: 'Consumable', color: '#d97706' },
  { key: 'EQUIPMENT', label: 'Equipment', color: '#6366f1' },
  { key: 'CROCKERY_CUTLERY', label: 'Crockery & Cutlery', color: '#0891b2' },
];

const TYPE_COLOR_MAP = Object.fromEntries(ITEM_TYPES.map((t) => [t.key, t.color]));

const ItemCategoryMaster = () => {
  const { theme } = useTheme();
  const c = colors[theme];

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({ category_name: '', item_type: 'INGREDIENT' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.item_type = typeFilter;
      const res = await api.getItemCategories(params);
      setCategories(res.data.categories || []);
    } catch (e) {
      console.error('Error loading item categories:', e);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleCreate = async () => {
    if (!createForm.category_name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.createItemCategory(createForm);
      setCreateForm({ category_name: '', item_type: createForm.item_type });
      setShowCreate(false);
      loadCategories();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (categoryId) => {
    setSaving(true);
    setError('');
    try {
      await api.updateItemCategory(categoryId, editForm);
      setEditingId(null);
      loadCategories();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      await api.toggleItemCategoryActive(cat.category_id);
      loadCategories();
    } catch (e) {
      console.error('Error toggling category:', e);
    }
  };

  const handleSortChange = async (cat, direction) => {
    const newOrder = cat.sort_order + direction;
    if (newOrder < 1) return;
    try {
      const other = categories.find(
        (cc) => cc.item_type === cat.item_type && cc.sort_order === newOrder
      );
      if (other) {
        await api.updateItemCategory(other.category_id, { sort_order: cat.sort_order });
      }
      await api.updateItemCategory(cat.category_id, { sort_order: newOrder });
      loadCategories();
    } catch (e) {
      console.error('Error updating sort order:', e);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.category_id);
    setEditForm({ category_name: cat.category_name, category_code: cat.category_code });
    setError('');
  };

  const filtered = categories.filter(
    (cc) =>
      cc.category_name.toLowerCase().includes(search.toLowerCase()) ||
      cc.category_code.toLowerCase().includes(search.toLowerCase())
  );

  // Group by item_type for display
  const grouped = {};
  for (const cat of filtered) {
    if (!grouped[cat.item_type]) grouped[cat.item_type] = [];
    grouped[cat.item_type].push(cat);
  }

  const activeCount = categories.filter((cc) => cc.is_active).length;
  const typeCounts = {};
  for (const cat of categories) {
    typeCounts[cat.item_type] = (typeCounts[cat.item_type] || 0) + 1;
  }

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="item-category-master-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-1" style={{ color: c.textPrimary }}>
              Item Categories
            </h1>
            <p className="text-sm" style={{ color: c.textSecondary }}>
              {categories.length} categories across {Object.keys(typeCounts).length} item types
              &mdash; {activeCount} active
            </p>
          </div>
          <button
            onClick={() => { setShowCreate(true); setError(''); }}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-indigo-600 hover:bg-indigo-700 transition-colors"
            data-testid="create-item-category-btn"
          >
            <Plus size={16} /> New Category
          </button>
        </div>
      </motion.div>

      {/* Type filter pills + search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={() => setTypeFilter('')}
          className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
          style={{
            background: !typeFilter ? c.textPrimary : c.cardBg,
            color: !typeFilter ? c.cardBg : c.textSecondary,
            border: `1px solid ${c.border}`,
          }}
          data-testid="filter-all"
        >
          All ({categories.length})
        </button>
        {ITEM_TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setTypeFilter(typeFilter === t.key ? '' : t.key)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            style={{
              background: typeFilter === t.key ? t.color + '18' : c.cardBg,
              color: typeFilter === t.key ? t.color : c.textSecondary,
              border: `1px solid ${typeFilter === t.key ? t.color + '40' : c.border}`,
            }}
            data-testid={`filter-${t.key}`}
          >
            {t.label} ({typeCounts[t.key] || 0})
          </button>
        ))}
        <div className="relative ml-auto max-w-xs w-full">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: c.textSecondary }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl"
            style={{ background: c.cardBg, color: c.textPrimary, border: `1px solid ${c.border}` }}
            data-testid="item-category-search"
          />
        </div>
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 px-4 py-3 rounded-xl flex items-center gap-2 text-sm"
            style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}
            data-testid="item-category-error"
          >
            <AlertCircle size={16} /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Form */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 rounded-xl p-5 overflow-hidden"
            style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme), border: `1px solid ${c.border}` }}
            data-testid="create-item-category-form"
          >
            <h3 className="font-semibold text-sm mb-3" style={{ color: c.textPrimary }}>New Category</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: c.textSecondary }}>Category Name *</label>
                <input
                  type="text"
                  value={createForm.category_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, category_name: e.target.value }))}
                  placeholder="e.g. Dairy & Alternatives"
                  className="w-full px-3 py-2 text-sm rounded-lg"
                  style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
                  data-testid="create-item-category-name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: c.textSecondary }}>Item Type *</label>
                <select
                  value={createForm.item_type}
                  onChange={(e) => setCreateForm((f) => ({ ...f, item_type: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg"
                  style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
                  data-testid="create-item-category-type"
                >
                  {ITEM_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={handleCreate}
                  disabled={saving || !createForm.category_name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="create-item-category-submit"
                >
                  <Check size={14} /> {saving ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm rounded-lg"
                  style={{ background: c.background, color: c.textSecondary }}
                  data-testid="create-item-category-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category Groups */}
      {loading ? (
        <div className="flex items-center justify-center h-40" data-testid="item-categories-loading">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12" data-testid="item-categories-empty">
          <Layers className="mx-auto mb-3" size={36} style={{ color: c.textSecondary }} />
          <p className="text-sm" style={{ color: c.textSecondary }}>
            {search || typeFilter ? 'No categories match your filters' : 'No categories found'}
          </p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="item-categories-list">
          {ITEM_TYPES.filter((t) => grouped[t.key]).map((t) => (
            <div key={t.key}>
              {/* Type Header */}
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: t.color }}
                />
                <h2 className="text-sm font-semibold" style={{ color: c.textPrimary }}>
                  {t.label}
                </h2>
                <span className="text-xs" style={{ color: c.textSecondary }}>
                  {grouped[t.key].length} categories
                </span>
              </div>
              {/* Table */}
              <div className="rounded-xl overflow-hidden" style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}>
                <table className="w-full text-left" data-testid={`table-${t.key}`}>
                  <thead>
                    <tr style={{ background: c.background, borderBottom: `1px solid ${c.border}` }}>
                      <th className="px-4 py-2.5 text-xs font-semibold w-16" style={{ color: c.textSecondary }}>#</th>
                      <th className="px-4 py-2.5 text-xs font-semibold" style={{ color: c.textSecondary }}>Category</th>
                      <th className="px-4 py-2.5 text-xs font-semibold" style={{ color: c.textSecondary }}>Code</th>
                      <th className="px-4 py-2.5 text-xs font-semibold text-center w-24" style={{ color: c.textSecondary }}>Status</th>
                      <th className="px-4 py-2.5 text-xs font-semibold text-right w-32" style={{ color: c.textSecondary }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[t.key].map((cat) => {
                      const isEditing = editingId === cat.category_id;
                      return (
                        <tr
                          key={cat.category_id}
                          className={`border-b transition-colors ${!cat.is_active ? 'opacity-50' : ''}`}
                          style={{ borderColor: c.border }}
                          data-testid={`item-row-${cat.category_code}`}
                        >
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-mono" style={{ color: c.textSecondary }}>
                                {cat.sort_order}
                              </span>
                              <div className="flex flex-col">
                                <button onClick={() => handleSortChange(cat, -1)} className="p-0.5 hover:opacity-70" style={{ color: c.textSecondary }} data-testid={`sort-up-${cat.category_code}`}>
                                  <ChevronUp size={11} />
                                </button>
                                <button onClick={() => handleSortChange(cat, 1)} className="p-0.5 hover:opacity-70" style={{ color: c.textSecondary }} data-testid={`sort-down-${cat.category_code}`}>
                                  <ChevronDown size={11} />
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.category_name}
                                onChange={(e) => setEditForm((f) => ({ ...f, category_name: e.target.value }))}
                                className="w-full px-2 py-1 text-sm rounded"
                                style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
                                data-testid="edit-item-category-name"
                              />
                            ) : (
                              <span className="text-sm font-medium" style={{ color: c.textPrimary }}>
                                {cat.category_name}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: c.background, color: c.textSecondary }}>
                              {cat.category_code}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <button
                              onClick={() => handleToggleActive(cat)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors"
                              style={{
                                background: cat.is_active ? '#dcfce7' : '#fef2f2',
                                color: cat.is_active ? '#166534' : '#991b1b',
                              }}
                              data-testid={`toggle-${cat.category_code}`}
                            >
                              {cat.is_active ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                              {cat.is_active ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {isEditing ? (
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => handleUpdate(cat.category_id)}
                                  disabled={saving}
                                  className="p-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                  data-testid="edit-item-save"
                                >
                                  <Check size={13} />
                                </button>
                                <button
                                  onClick={() => { setEditingId(null); setError(''); }}
                                  className="p-1.5 rounded-lg"
                                  style={{ background: c.background, color: c.textSecondary }}
                                  data-testid="edit-item-cancel"
                                >
                                  <X size={13} />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit(cat)}
                                className="p-1.5 rounded-lg hover:opacity-70"
                                style={{ color: c.accentBlue }}
                                data-testid={`edit-btn-${cat.category_code}`}
                              >
                                <Edit2 size={13} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-5 text-xs" style={{ color: c.textSecondary }}>
        Item categories classify every object a restaurant consumes, uses, and tracks.
        Downstream systems sync via{' '}
        <code className="px-1 py-0.5 rounded" style={{ background: c.background }}>
          GET /api/master-data/item-categories?item_type=INGREDIENT&active_only=true
        </code>
      </div>
    </div>
  );
};

export default ItemCategoryMaster;
